import { useValidSearchParams } from '@/features/shared/hooks/search'
import { useCurrentUniversalCategory } from '@/features/shop/services/UniversalCategories/hooks'
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { type SearchParams } from '@/features/shop/services/Search/types'
import {
  fetchInfiniteUniversalSearchProductsWithZip,
  fetchUniversalSearchProductsWithZip,
} from '@/features/shop/services/UniversalCategories/fetchers'
import { useSearchParams } from 'next/navigation'

const UniversalProductSearchQuery = 'Universal product search'
const InfiniteUniversalProductSearchQuery = 'Infinite Universal product search'

export const universalSearchQueryOptions = (params: PaginatedParams) =>
  queryOptions({
    queryKey: [UniversalProductSearchQuery, params] as const,
    queryFn: fetchUniversalSearchProductsWithZip,
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
  })

export const useQueryUniversalSearch = (additionalParams?: SearchParams) => {
  const searchParams = useValidSearchParams()
  const page = useSearchParams()?.get('page') ?? '1'

  const pageAsNumber = isNaN(+page) ? 1 : +page

  const category = useCurrentUniversalCategory()
  const categoryId = additionalParams?.category_id ?? category?.id ?? 0
  const params: PaginatedParams = {
    ...searchParams,
    ...additionalParams,
    category_id: categoryId,
    pageParam: pageAsNumber,
  }

  return useQuery({
    ...universalSearchQueryOptions(params),
    enabled: Boolean(categoryId),
  })
}

export type UniversalSearchQueryKey = readonly [typeof UniversalProductSearchQuery, PaginatedParams]

type PaginatedParams = SearchParams & { pageParam?: number }

export const infiniteUniversalSearchQueryOptions = (params: SearchParams) =>
  infiniteQueryOptions({
    queryKey: [InfiniteUniversalProductSearchQuery, params] as const,
    queryFn: fetchInfiniteUniversalSearchProductsWithZip,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page = 0, total_hits = 0, hits_per_page = 0 } = lastPage
      if (total_hits - page * hits_per_page <= 0) return undefined
      return page + 1
    },
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
  })

export const useInfiniteQueryUniversalSearch = (additionalParams?: SearchParams) => {
  const searchParams = useValidSearchParams()

  const category = useCurrentUniversalCategory()
  const categoryId = additionalParams?.category_id ?? category?.id ?? 0
  const params: SearchParams = {
    ...searchParams,
    ...additionalParams,
    category_id: categoryId,
  }

  return useInfiniteQuery({
    ...infiniteUniversalSearchQueryOptions(params),
    enabled: Boolean(categoryId),
  })
}

export type InfiniteUniversalSearchQueryKey = readonly [
  typeof InfiniteUniversalProductSearchQuery,
  SearchParams,
]
